import { useMemo } from 'react'
import {
  Activity, Baby, Bell, Brain, Building2, Calendar, Droplets, FileText,
  Heart, HeartPulse, List, Microscope, Pill, Shield,
  Stethoscope, Sun, Truck, UserCheck, Users, Wind, Zap,
} from 'lucide-react'
import BSDCard from '../BSDCard'
import AutoGrid from '../AutoGrid'
import DistrictMap from '../DistrictMap'
import { type AmbitoId } from '../../data/ambitiStats'
import { FARMACIE, FARMACIE_SERVICE_KEYS } from '../../data/farmacie'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts'
import { CHART_TOOLTIP_STYLE, formatInt } from '../../utils/dashboardFormatters'
import FarmacieDialogList from './FarmacieDialogList'

const FARMACIE_PALETTE = [
  '#059669', '#10b981', '#0d9488', '#0891b2', '#0ea5e9',
  '#7c3aed', '#db2777', '#d97706', '#dc2626', '#65a30d',
  '#0284c7', '#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444',
]

const SERVICE_ICONS: Record<string, React.ComponentType<{ size?: number | string; style?: React.CSSProperties }>> = {
  'Prenotazione di visite specialistiche ed esami di laboratorio (CUP)': Calendar,
  'Servizio di scelta/revoca medico di medicina generale': UserCheck,
  'Servizio Fascicolo Sanitario Elettronico (attivazione, arricchimento, consultazione)': FileText,
  'APP NOTIFY (registrazione in farmacia del numero di telefono dei cittadini destinato alla ricezione dei codici OTP e NRE)': Bell,
  'Partecipazione campagna antinfluenzale (vaccinazione antinfluenzale)': Shield,
  'Partecipazione campagna anti Covid-19 (vaccinazione anti SARS-COV2)': Shield,
  'Allestimento terapie personalizzate (deblistering)': Pill,
  'Misurazione della capacità polmonare tramite auto-spirometria': Wind,
  'Servizio di consegna a domicilio di farmaci in orario diurno e festivo': Truck,
  'Consulenza nutrizionale (nutrizionista)': Activity,
  'Consulenza psicologica (psicologo)': Brain,
  'Presenza di dispositivi semiautomatici per la defibrillazione (DAE)': Zap,
  'Infermiere': Stethoscope,
  'Fisioterapista': Activity,
  'Altri operatori sanitari (esclusi medici, veterinari e odontoiatri)': Users,
  'Misurazione della pressione arteriosa': HeartPulse,
  "Misurazione non invasiva della saturazione percentuale dell'ossigeno": HeartPulse,
  'Screening colon-retto per la rilevazione di sangue occulto nelle feci': Microscope,
  'Elettrocardiogramma (ECG)': Heart,
  'Holter pressorio': Heart,
  'Holter cardiaco': Heart,
  'Test per glicemia': Droplets,
  'Test per profilo lipidico, colesterolo totale, HDL, LDL e trigliceridi': Droplets,
  'Test per misurazione emoglobina,creatinina, transaminasi, ematocrito, emoglobina glicata': Droplets,
  'Test per la misurazione di componenti delle urine (acido ascorbico, chetoni, urobilinogeno, bilirubina, leucociti, nitriti, PH, sangue, proteine)': Droplets,
  'Test ovulazione': Activity,
  'Test di gravidanza': Baby,
  "Test per menopausa per la misurazione dell'ormone FSH nelle urine": Activity,
  'test Helicobacter pylori': Microscope,
  'Test vitamina D': Sun,
  'test Proteina C reattiva': Microscope,
  'Tampone PCR Molecolare Covid-19': Microscope,
  'Tampone antigenico Covid-19': Microscope,
  'Test sierologico Covid-19': Microscope,
  'Tampone faringeo streptococco': Microscope,
}

const SERVICE_SHORT_LABELS: Record<string, string> = {
  'Prenotazione di visite specialistiche ed esami di laboratorio (CUP)': 'CUP',
  'Servizio di scelta/revoca medico di medicina generale': 'Scelta/revoca medico',
  'Servizio Fascicolo Sanitario Elettronico (attivazione, arricchimento, consultazione)': 'Fascicolo Sanitario',
  'APP NOTIFY (registrazione in farmacia del numero di telefono dei cittadini destinato alla ricezione dei codici OTP e NRE)': 'APP NOTIFY',
  'Partecipazione campagna antinfluenzale (vaccinazione antinfluenzale)': 'Vaccino antinfluenzale',
  'Partecipazione campagna anti Covid-19 (vaccinazione anti SARS-COV2)': 'Vaccino Covid-19',
  'Allestimento terapie personalizzate (deblistering)': 'Deblistering',
  'Misurazione della capacità polmonare tramite auto-spirometria': 'Auto-spirometria',
  'Servizio di consegna a domicilio di farmaci in orario diurno e festivo': 'Consegna domicilio',
  'Consulenza nutrizionale (nutrizionista)': 'Nutrizionista',
  'Consulenza psicologica (psicologo)': 'Psicologo',
  'Presenza di dispositivi semiautomatici per la defibrillazione (DAE)': 'DAE',
  'Infermiere': 'Infermiere',
  'Fisioterapista': 'Fisioterapista',
  'Altri operatori sanitari (esclusi medici, veterinari e odontoiatri)': 'Altri operatori sanitari',
  'Misurazione della pressione arteriosa': 'Pressione arteriosa',
  "Misurazione non invasiva della saturazione percentuale dell'ossigeno": 'Saturazione ossigeno',
  'Screening colon-retto per la rilevazione di sangue occulto nelle feci': 'Screening colon-retto',
  'Elettrocardiogramma (ECG)': 'ECG',
  'Holter pressorio': 'Holter pressorio',
  'Holter cardiaco': 'Holter cardiaco',
  'Test per glicemia': 'Test glicemia',
  'Test per profilo lipidico, colesterolo totale, HDL, LDL e trigliceridi': 'Test colesterolo/lipidi',
  'Test per misurazione emoglobina,creatinina, transaminasi, ematocrito, emoglobina glicata': 'Test emoglobina',
  'Test per la misurazione di componenti delle urine (acido ascorbico, chetoni, urobilinogeno, bilirubina, leucociti, nitriti, PH, sangue, proteine)': 'Test urine',
  'Test ovulazione': 'Test ovulazione',
  'Test di gravidanza': 'Test gravidanza',
  "Test per menopausa per la misurazione dell'ormone FSH nelle urine": 'Test menopausa',
  'test Helicobacter pylori': 'Test H. pylori',
  'Test vitamina D': 'Test vitamina D',
  'test Proteina C reattiva': 'Test proteina C',
  'Tampone PCR Molecolare Covid-19': 'Tampone PCR Covid',
  'Tampone antigenico Covid-19': 'Tampone antigenico Covid',
  'Test sierologico Covid-19': 'Test sierologico Covid',
  'Tampone faringeo streptococco': 'Tampone streptococco',
  'Altri eventuali Servizi Offerti': 'Altri servizi',
}

const SERVICE_DESCRIPTIONS: Record<string, string> = {
  'Prenotazione di visite specialistiche ed esami di laboratorio (CUP)': 'Prenotazione di visite mediche specialistiche ed esami di laboratorio tramite il Centro Unico di Prenotazione.',
  'Servizio di scelta/revoca medico di medicina generale': 'Possibilità di scegliere o revocare il proprio medico di medicina generale direttamente in farmacia.',
  'Servizio Fascicolo Sanitario Elettronico (attivazione, arricchimento, consultazione)': 'Attivazione, arricchimento e consultazione del Fascicolo Sanitario Elettronico personale.',
  'APP NOTIFY (registrazione in farmacia del numero di telefono dei cittadini destinato alla ricezione dei codici OTP e NRE)': 'Registrazione del numero di telefono per la ricezione di codici OTP e NRE tramite l\'app NOTIFY.',
  'Partecipazione campagna antinfluenzale (vaccinazione antinfluenzale)': 'Somministrazione del vaccino antinfluenzale nell\'ambito delle campagne di prevenzione regionali.',
  'Partecipazione campagna anti Covid-19 (vaccinazione anti SARS-COV2)': 'Somministrazione del vaccino anti SARS-CoV-2 nell\'ambito delle campagne di vaccinazione regionali.',
  'Allestimento terapie personalizzate (deblistering)': 'Preparazione di terapie farmacologiche personalizzate per pazienti con politerapia, mediante organizzazione delle dosi giornaliere.',
  'Misurazione della capacità polmonare tramite auto-spirometria': 'Misurazione della funzionalità respiratoria tramite spirometro per la valutazione della capacità polmonare.',
  'Servizio di consegna a domicilio di farmaci in orario diurno e festivo': 'Consegna a domicilio di farmaci e prodotti sanitari anche in orario diurno e nei giorni festivi.',
  'Consulenza nutrizionale (nutrizionista)': 'Consulenza professionale con nutrizionista per piani alimentari personalizzati e supporto alla salute.',
  'Consulenza psicologica (psicologo)': 'Consulenza psicologica con professionista abilitato per supporto emotivo e benessere mentale.',
  'Presenza di dispositivi semiautomatici per la defibrillazione (DAE)': 'Disponibilità di defibrillatori semiautomatici (DAE) per intervento d\'emergenza in caso di arresto cardiaco.',
  'Infermiere': 'Presenza di personale infermieristico per prestazioni sanitarie ambulatoriali e assistenza al paziente.',
  'Fisioterapista': 'Presenza di fisioterapista per valutazioni e trattamenti riabilitativi presso la farmacia.',
  'Altri operatori sanitari (esclusi medici, veterinari e odontoiatri)': 'Presenza di altri professionisti sanitari quali ottici, podologi, logopedisti e simili.',
  'Misurazione della pressione arteriosa': 'Misurazione della pressione arteriosa con strumentazione professionale per il monitoraggio cardiovascolare.',
  "Misurazione non invasiva della saturazione percentuale dell'ossigeno": 'Misurazione non invasiva della saturazione di ossigeno nel sangue tramite pulsossimetro.',
  'Screening colon-retto per la rilevazione di sangue occulto nelle feci': 'Test per la ricerca di sangue occulto nelle feci come screening per la prevenzione del tumore al colon-retto.',
  'Elettrocardiogramma (ECG)': 'Registrazione dell\'attività elettrica del cuore tramite elettrocardiogramma per la diagnosi di anomalie cardiache.',
  'Holter pressorio': 'Monitoraggio continuo della pressione arteriosa nell\'arco delle 24 ore tramite dispositivo Holter.',
  'Holter cardiaco': 'Monitoraggio continuo dell\'attività cardiaca nell\'arco delle 24 ore tramite registratore Holter.',
  'Test per glicemia': 'Misurazione rapida del livello di glucosio nel sangue per il monitoraggio del diabete.',
  'Test per profilo lipidico, colesterolo totale, HDL, LDL e trigliceridi': 'Analisi del profilo lipidico completo per la valutazione del rischio cardiovascolare.',
  'Test per misurazione emoglobina,creatinina, transaminasi, ematocrito, emoglobina glicata': 'Test ematici rapidi per la valutazione di parametri come emoglobina, creatinina e transaminasi.',
  'Test per la misurazione di componenti delle urine (acido ascorbico, chetoni, urobilinogeno, bilirubina, leucociti, nitriti, PH, sangue, proteine)': 'Analisi delle urine per la valutazione di parametri chimici e la diagnosi di infezioni o patologie renali.',
  'Test ovulazione': 'Test rapido per determinare il periodo fertile della donna tramite rilevazione del picco di LH nelle urine.',
  'Test di gravidanza': 'Test rapido per la conferma della gravidanza tramite rilevazione dell\'ormone hCG nelle urine.',
  "Test per menopausa per la misurazione dell'ormone FSH nelle urine": 'Test per la valutazione della menopausa tramite misurazione dell\'ormone FSH nelle urine.',
  'test Helicobacter pylori': 'Test per la diagnosi dell\'infezione da Helicobacter pylori responsabile di ulcere gastriche.',
  'Test vitamina D': 'Test rapido per la misurazione dei livelli di vitamina D nel sangue.',
  'test Proteina C reattiva': 'Test per la misurazione della proteina C reattiva come marcatore di infiammazione.',
  'Tampone PCR Molecolare Covid-19': 'Tampone molecolare PCR per la diagnosi di infezione da SARS-CoV-2.',
  'Tampone antigenico Covid-19': 'Tampone antigenico rapido per la diagnosi di infezione da SARS-CoV-2.',
  'Test sierologico Covid-19': 'Test sierologico per la rilevazione di anticorpi contro SARS-CoV-2.',
  'Tampone faringeo streptococco': 'Tampone faringeo per la diagnosi rapida di infezione da Streptococco beta-emolitico di gruppo A.',
  'Altri eventuali Servizi Offerti': 'Altri servizi sanitari e di consulenza offerti dalla farmacia.',
}

const AXIS_LABEL_MAX_CHARS = 20
const AXIS_LABEL_WIDTH = 130

function wrapAxisLabel(value: string, maxChars = AXIS_LABEL_MAX_CHARS): string[] {
  const clean = value.trim()
  if (!clean) return ['']
  const words = clean.split(/\s+/)
  const lines: string[] = []
  let currentLine = ''
  for (const word of words) {
    if (!currentLine) { currentLine = word; continue }
    if (`${currentLine} ${word}`.length <= maxChars) {
      currentLine = `${currentLine} ${word}`
    } else {
      lines.push(currentLine)
      currentLine = word
    }
  }
  if (currentLine) lines.push(currentLine)
  return lines
}

function FarmacieBarTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ value?: number; payload?: { name?: string; fullName?: string; value?: number } }>
}) {
  if (!active || !payload || payload.length === 0) return null
  const point = payload[0]
  const nome = point?.payload?.fullName ?? point?.payload?.name ?? 'Servizio'
  const valore = point?.payload?.value ?? point?.value ?? 0
  return (
    <div style={{
      background: 'var(--tooltip-bg)',
      border: '1px solid var(--tooltip-border)',
      borderRadius: 8,
      fontSize: 12,
      color: 'var(--tooltip-text)',
      padding: '8px 10px',
      maxWidth: 280,
    }}>
      <div style={{ color: 'var(--text-0)', fontWeight: 600, marginBottom: 2, whiteSpace: 'normal', lineHeight: 1.4 }}>{nome}</div>
      <div style={{ color: 'var(--text-2)' }}>{`Farmacie: ${formatInt(Number(valore))}`}</div>
    </div>
  )
}

function LeftAlignedAxisTick({ y = 0, payload }: { y?: number; payload?: { value?: string | number } }) {
  const lines = wrapAxisLabel(String(payload?.value ?? ''))
  return (
    <text x={0} y={y} fill="var(--text-2)" fontSize={10} textAnchor="start">
      {lines.map((line, index) => (
        <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 4 : 11}>{line}</tspan>
      ))}
    </text>
  )
}

export default function FarmacieSection({
  selectedAmbitoId,
  onSelectedAmbitoChange,
}: {
  selectedAmbitoId: AmbitoId | null
  onSelectedAmbitoChange: (ambitoId: AmbitoId | null) => void
}) {
  const farmacieData = useMemo(() => {
    const scope = selectedAmbitoId
      ? FARMACIE.filter(f => f.ambitoId === selectedAmbitoId)
      : FARMACIE

    const perServizio = FARMACIE_SERVICE_KEYS.map((key, index) => {
      const farmacieCheOffrono = scope.filter(f => f.servizi[key] && f.servizi[key].trim() !== '')
      return {
        id: `servizio-${index}`,
        key,
        shortLabel: SERVICE_SHORT_LABELS[key] ?? key,
        value: farmacieCheOffrono.length,
        color: FARMACIE_PALETTE[index % FARMACIE_PALETTE.length],
        farmacie: farmacieCheOffrono,
      }
    }).filter(s => s.value > 0)

    const comunali = scope.filter(f => f.comunale)

    return { totale: scope.length, scope, perServizio, comunali }
  }, [selectedAmbitoId])

  return (
    <>
      <h3 className="telemetry-section-title" style={{ marginBottom: 8 }}>Farmacie dei Servizi</h3>

      <AutoGrid style={{ marginBottom: 14 }}>

        <BSDCard className="telemetry-map-card telemetry-cdc-map-card" kind="panel" sezione="Farmacie" style={{ minHeight: 120, padding: 10 }} zoomable>
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Seleziona l'ambito</h3>
          <DistrictMap
            onSelectDistrict={(id) => onSelectedAmbitoChange(id as AmbitoId)}
            onClearSelection={() => onSelectedAmbitoChange(null)}
            selectedDistrictId={selectedAmbitoId ?? null}
            showLegend={false}
            showInstructionText={false}
          />
        </BSDCard>

        <BSDCard
          kind="kpi"
          sezione="Farmacie"
          label="Farmacie"
          value={formatInt(farmacieData.totale)}
          zoomable
          hideZoomButton
          sub={
            <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
              Le <strong>Farmacie dei Servizi</strong> sono punti di riferimento sanitario sul territorio che, oltre alla dispensazione dei farmaci, erogano servizi di prevenzione, monitoraggio e supporto alla salute dei cittadini.
            </p>
          }
          zoomSub={<FarmacieDialogList farmacie={farmacieData.scope} />}
          footer={(openZoom) => (
            <button
              type="button"
              onClick={openZoom}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                width: '100%', padding: '6px 0', fontSize: 12, fontWeight: 600,
                color: 'var(--accent)', background: 'none', border: 'none',
                borderTop: '1px solid var(--border)', cursor: 'pointer',
                justifyContent: 'center', marginTop: 8, paddingTop: 8,
              }}
            >
              <List size={14} />
              Vedi elenco completo
            </button>
          )}
        />

        <BSDCard kind="panel" zoomable sezione="Farmacie" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Servizi offerti</h3>
          <div style={{ flex: 1, minHeight: 0, maxHeight: 320, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
            <ResponsiveContainer width="100%" height={Math.max(220, farmacieData.perServizio.length * 34)}>
              <BarChart data={farmacieData.perServizio.map(s => ({ ...s, name: s.shortLabel, fullName: s.key }))} layout="vertical" margin={{ top: 4, right: 28, bottom: 4, left: 6 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={<LeftAlignedAxisTick />} axisLine={false} tickLine={false} width={AXIS_LABEL_WIDTH} />
                <Tooltip content={<FarmacieBarTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={14}>
                  <LabelList dataKey="value" position="right" formatter={(v: unknown) => formatInt(Number(v ?? 0))} fill="var(--text-2)" fontSize={11} />
                  {farmacieData.perServizio.map((entry, idx) => (
                    <Cell key={`cell-${entry.id}-${idx}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BSDCard>

        <BSDCard
          kind="kpi"
          sezione="Farmacie"
          label="Farmacie Comunali"
          value={formatInt(farmacieData.comunali.length)}
          icon={Building2}
          color="#0891b2"
          zoomable
          hideZoomButton
          sub={
            <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
              Farmacie di proprietà comunale che erogano servizi sanitari sul territorio per conto dei Comuni della provincia di Monza e Brianza.
            </p>
          }
          zoomSub={<FarmacieDialogList farmacie={farmacieData.comunali} />}
          footer={(openZoom) => (
            <button
              type="button"
              onClick={openZoom}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                width: '100%', padding: '6px 0', fontSize: 11, fontWeight: 600,
                color: '#0891b2', background: 'none', border: 'none',
                borderTop: '1px solid var(--border)', cursor: 'pointer',
                justifyContent: 'center', marginTop: 8, paddingTop: 8,
              }}
            >
              <List size={14} />
              Vedi elenco completo
            </button>
          )}
        />

        {farmacieData.perServizio.map(({ id, key, shortLabel, value, color, farmacie }) => {
          const Icon = SERVICE_ICONS[key]
          return (
            <BSDCard
              key={id}
              kind="kpi"
              sezione="Farmacie"
              label={shortLabel}
              value={formatInt(value)}
              icon={Icon}
              color={color}
              zoomable
              hideZoomButton
              sub={
                <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
                  {SERVICE_DESCRIPTIONS[key] ?? `Farmacie che offrono il servizio "${shortLabel}".`}
                </p>
              }
              zoomSub={<FarmacieDialogList farmacie={farmacie} />}
              footer={(openZoom) => (
                <button
                  type="button"
                  onClick={openZoom}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    width: '100%', padding: '6px 0', fontSize: 11, fontWeight: 600,
                    color, background: 'none', border: 'none',
                    borderTop: '1px solid var(--border)', cursor: 'pointer',
                    justifyContent: 'center', marginTop: 8, paddingTop: 8,
                  }}
                >
                  <List size={14} />
                  Vedi elenco completo
                </button>
              )}
            />
          )
        })}
      </AutoGrid>
    </>
  )
}
